/**
 * MER-588: point-in-time snapshot tiles read the LATEST snapshot period only,
 * then combine across divisions.
 *
 * THE DEFECT. financial_health carries one snapshot row per division per
 * snapshot period (period_type = 'snapshot'). The AR, unapplied-cash and EMR
 * tiles aggregate those rows with `sum`. A balance is a point-in-time figure,
 * so summing two snapshot periods adds the same receivable twice. The tiles
 * read correctly today only because production holds a single snapshot
 * period; the day a second one is retained, every one of them silently reads
 * roughly double. `latest` is not the answer either: it orders by period and
 * takes ONE row, which is one arbitrary division (the MER-370 / MER-464 trap).
 *
 * THE RULE (the ticket's option 2). Restrict the rows to the newest snapshot
 * period the tenant has, then let the widget's own aggregation run over what
 * is left: `sum` adds one balance per division, a DERIVED_RATIOS SUM/SUM
 * (delinquency) divides one snapshot's dollars by the same snapshot's dollars,
 * and `avg` averages one figure per division. The restriction is a row filter,
 * not an aggregation, so every render path that already calls
 * resolveLatestFilters + applyFilters (KPI tiles, comparison panes, chart
 * series) inherits it without learning a new aggregation.
 *
 * WHY ONE PERIOD, NOT EACH DIVISION'S NEWEST. normalize.py stamps every
 * snapshot row of a refresh with the same period, and every division comes
 * from one AR export and one contracts export, so on a healthy refresh the two
 * rules select the same rows. They differ only when something went wrong, and
 * there the global period is the honest one: a division missing from the newest
 * export has no open balance to report, and a relabeled division would
 * otherwise keep its old label's balance beside the new one, which is the
 * double count this ticket removes. Taking each division's own newest row would
 * keep a stale balance in the total for ever with nothing on screen to say so.
 * The MER-589 `latest` pin on asset rows made the same call for the same reason
 * (a retired truck must stop counting).
 *
 * WHEN IT APPLIES. Only when the widget pins `period_type` to 'snapshot'. A
 * widget without that pin may be reading monthly rows through a snapshot-mode
 * timeline (Collection Rate divides monthly cash by monthly invoicing), and
 * restricting it to snapshot rows would empty it.
 *
 * Pure and dependency-free so node:test imports it directly; the .d.mts beside
 * it types it for queries.ts.
 */

/** Relations whose snapshot rows need the latest-period restriction. */
export const SNAPSHOT_SOURCES = {
  financial_health: {
    period: "period_date",
    marker: { column: "period_type", value: "snapshot" },
  },
};

/**
 * How long one latest-period lookup is shared across tiles. Long enough that a
 * page load of snapshot tiles runs one lookup; short enough that a tab left open
 * across a refresh that replaced the snapshot period stops filtering to a period
 * that no longer exists within a minute, instead of reading empty until reload.
 */
export const SNAPSHOT_PERIOD_TTL_MS = 60_000;

/** True while a lookup started at `startedAt` may still be reused at `now`. */
export function isSnapshotLookupFresh(startedAt, now) {
  return (
    Number.isFinite(startedAt) && Number.isFinite(now) && now - startedAt < SNAPSHOT_PERIOD_TTL_MS
  );
}

/**
 * The snapshot spec for `table` when the widget filter pins the snapshot
 * marker, else null.
 */
export function latestSnapshotSpec(table, filter) {
  const spec = SNAPSHOT_SOURCES[String(table)];
  if (!spec) return null;
  const raw = filter ? filter[spec.marker.column] : undefined;
  if (raw === spec.marker.value) return spec;
  if (raw && typeof raw === "object" && raw.op === "eq" && raw.value === spec.marker.value) {
    return spec;
  }
  return null;
}

/**
 * The newest snapshot period across `rows`, or null when no row carries one.
 * Periods are ISO dates, so string order is date order.
 */
export function latestSnapshotPeriod(rows, spec) {
  let newest = null;
  for (const row of rows ?? []) {
    const period = row?.[spec.period];
    if (period === null || period === undefined || period === "") continue;
    const p = String(period);
    if (newest === null || p > newest) newest = p;
  }
  return newest;
}
